import { ChangeDetectionStrategy, Component, inject, input, output, viewChild } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { ConfirmationService } from '@services/confirmation.service';
import type { PriceListOption } from '../pricing-grid/pricing-grid.model';

/**
 * The price-list (year) dropdown every water pricing grid is scoped to, with the "switching discards
 * your unsaved edits" guard those grids share: while {@link hasUnsavedChanges} holds, a switch is
 * confirmed first and a cancel leaves both the dropdown and the loaded grid where they were.
 *
 * {@link priceListChange} therefore only ever emits a switch the user has agreed to — the parent's
 * handler can load the new grid unconditionally.
 */
@Component({
  selector: 'app-price-list-select',
  templateUrl: './price-list-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatFormFieldModule, MatSelectModule],
})
export class PriceListSelectComponent {
  readonly priceLists = input.required<readonly PriceListOption[]>();
  /** The currently *loaded* price list — what a rejected switch reverts to. */
  readonly selectedId = input.required<number | null>();
  /** Already-translated, so each section keeps its own wording. */
  readonly label = input.required<string>();
  readonly discardTitle = input.required<string>();
  readonly discardPrompt = input.required<string>();
  /** Whether switching would throw away edits, i.e. whether to confirm first. */
  readonly hasUnsavedChanges = input(false);
  readonly disabled = input(false);

  /** A confirmed switch to another price list. */
  readonly priceListChange = output<number>();

  private readonly confirmation = inject(ConfirmationService);
  private readonly select = viewChild('select', { read: MatSelect });

  protected async onSelectionChange(priceListId: number): Promise<void> {
    if (priceListId === this.selectedId()) return;

    if (this.hasUnsavedChanges()) {
      const confirmed = await this.confirmation.open({
        title: this.discardTitle(),
        prompt: this.discardPrompt(),
      });
      if (!confirmed) {
        // Revert to the still-loaded price list. The `selectedId` input already holds it, so re-rendering
        // is a no-op and would leave mat-select on the rejected option — push the value straight onto the
        // control instead. `value` does not re-emit selectionChange, so this does not re-enter here.
        const select = this.select();
        if (select) select.value = this.selectedId();
        return;
      }
    }

    this.priceListChange.emit(priceListId);
  }
}
